import { Alert, App, Form, List, Modal, Radio, Typography } from "antd"
import React, { useEffect, useState } from "react"
import { useTranslation } from "react-i18next"

import { useDateFormatter } from "../../../hooks/use-date-formatter.tsx"
import { useAppDispatch } from "../../../hooks/use-app-dispatch.ts"
import { useAppSelector } from "../../../hooks/use-app-selector.ts"
import {
  LEAD_STATUSES,
  LEAD_STATUSES_MAP
} from "../../Lead/constants/lead-statuses.ts"
import { ARCHIVE_LEAD_STATUSES } from "../constants/archive-lead-allowed-statuses.ts"
import { lossReasonRules } from "../constants/loss-reason.ts"
import { LEADS_SAVE_RESULT } from "../constants/leads-save-results.ts"
import { archiveLead } from "../redux/leads.thunks.ts"
import type { PendingCommitment } from "../api/services.types.ts"
import { TextArea } from "../../../components/Input/TextArea.tsx"

const { Text } = Typography

interface ArchiveLeadFormData {
  target_status: number
  lost_reason?: string
  lost_notes?: string
}

interface PendingCommitmentsRejection {
  type?: string
  message?: string
  pending_commitments?: PendingCommitment[]
}

interface ArchiveLeadModalProps {
  leadId: string
  leadName?: string | null
  open: boolean
  onCancel: () => void
  onArchived?: () => void
}

export function ArchiveLeadModal({
  leadId,
  leadName,
  open,
  onCancel,
  onArchived
}: ArchiveLeadModalProps) {
  const { t } = useTranslation()
  const { message } = App.useApp()
  const dispatch = useAppDispatch()
  const { formatDate } = useDateFormatter()
  const saving = useAppSelector((state) => state.leads.saveFetching)
  const [form] = Form.useForm<ArchiveLeadFormData>()
  const targetStatus = Form.useWatch("target_status", form)
  // Picking a different outcome asks the server a different question, so the
  // previous answer — and the confirmation riding on it — cannot carry over.
  // Without this, changing the radio after a refusal would send
  // `cancel_appointments` on the *first* request for the new outcome.
  useEffect(() => {
    setCommitments(null)
  }, [targetStatus])
  // Set when the server refuses because the lead still has meetings booked.
  // Holding them here is what turns the second click into "yes, cancel them"
  // rather than a repeat of the request the server already declined.
  const [commitments, setCommitments] = useState<PendingCommitment[] | null>(
    null
  )

  useEffect(() => {
    if (!open) {
      form.resetFields()
      setCommitments(null)
    }
  }, [form, open])

  const archive = async (cancelAppointments: boolean) => {
    let values: ArchiveLeadFormData
    try {
      values = await form.validateFields()
    } catch {
      // Sharing the catch below would clear the meeting list being confirmed.
      return
    }

    try {
      // Only Lost carries a loss reason. Sending an empty one on the other
      // outcomes wipes whatever the lead already held.
      const lostReason =
        values.target_status === LEAD_STATUSES.LOST
          ? { lost_reason: values.lost_reason?.trim() }
          : {}
      await dispatch(
        archiveLead({
          id: leadId,
          target_status: values.target_status,
          ...lostReason,
          lost_notes: values.lost_notes?.trim() || "",
          ...(cancelAppointments ? { cancel_appointments: true } : {})
        })
      ).unwrap()
      form.resetFields()
      setCommitments(null)
      onArchived?.()
    } catch (rejection) {
      const pending = rejection as PendingCommitmentsRejection | undefined
      if (pending?.type !== LEADS_SAVE_RESULT.PENDING_COMMITMENTS) {
        // Anything else — a 500, a dropped connection, a missing field — has
        // already been reported by the thunk. Leaving confirm mode up would
        // pin the modal to a meeting list that no longer describes the state
        // and keep re-sending the confirmation.
        setCommitments(null)
        return
      }
      const listed = pending.pending_commitments ?? []
      if (!listed.length) {
        // A refusal we cannot explain: the modal would render no alert, keep
        // its original footer, and re-send the identical refused request on
        // the next click. Say what happened instead of looking frozen.
        setCommitments(null)
        message.warning(
          pending.message || t("leadArchive.commitments.unavailable")
        )
        return
      }
      setCommitments(listed)
      return
    }
  }

  const displayName = leadName?.trim() || t("leadArchive.thisLead")
  const hasCommitments = !!commitments?.length

  return (
    <Modal
      open={open}
      title={t("leadArchive.title", { name: displayName })}
      okText={
        hasCommitments
          ? t("leadArchive.commitments.confirm")
          : t("leadArchive.confirm")
      }
      cancelText={
        hasCommitments
          ? t("leadArchive.commitments.keepActive")
          : t("leadArchive.cancel")
      }
      okButtonProps={{ danger: true }}
      confirmLoading={saving}
      cancelButtonProps={{ disabled: saving }}
      closable={!saving}
      keyboard={!saving}
      maskClosable={false}
      onOk={() => archive(hasCommitments)}
      onCancel={onCancel}
      destroyOnHidden
    >
      <Alert
        type="warning"
        showIcon
        className="mb-4"
        message={t("leadArchive.impact")}
      />

      {hasCommitments && (
        <Alert
          type="error"
          showIcon
          className="mb-4"
          data-testid="archive-pending-commitments"
          message={t("leadArchive.commitments.title")}
          description={
            <>
              <div className="mb-2">
                {t("leadArchive.commitments.body", {
                  count: commitments?.length ?? 0
                })}
              </div>
              <List
                size="small"
                dataSource={commitments ?? []}
                renderItem={(commitment) => (
                  <List.Item className="!px-0">
                    <div className="flex flex-col">
                      <Text strong>{commitment.title}</Text>
                      <Text type="secondary" className="text-xs">
                        {formatDate(commitment.start_at)}
                        {commitment.has_external_invitees
                          ? ` · ${t("leadArchive.commitments.externalInvitee")}`
                          : ""}
                      </Text>
                    </div>
                  </List.Item>
                )}
              />
            </>
          }
        />
      )}

      <Form form={form} layout="vertical" disabled={saving}>
        <Form.Item<ArchiveLeadFormData>
          name="target_status"
          label={t("leadArchive.targetStatus")}
          rules={[
            {
              required: true,
              message: t("leadArchive.targetStatusRequired")
            }
          ]}
        >
          <Radio.Group className="flex flex-col gap-2">
            {ARCHIVE_LEAD_STATUSES.map((status) => (
              <Radio key={status} value={status}>
                {t(`leadArchive.statuses.${LEAD_STATUSES_MAP[status].label}`)}
              </Radio>
            ))}
          </Radio.Group>
        </Form.Item>

        {targetStatus === LEAD_STATUSES.LOST && (
          <Form.Item<ArchiveLeadFormData>
            name="lost_reason"
            label={t("leadArchive.lossReason")}
            preserve={false}
            rules={lossReasonRules(t("leadArchive.lossReasonRequired"))}
          >
            <TextArea
              rows={3}
              placeholder={t("leadArchive.lossReasonPlaceholder")}
            />
          </Form.Item>
        )}

        <Form.Item<ArchiveLeadFormData>
          name="lost_notes"
          label={t("leadArchive.notes")}
          className="mb-0"
        >
          <TextArea
            rows={3}
            placeholder={t("leadArchive.notesPlaceholder")}
          />
        </Form.Item>
      </Form>
    </Modal>
  )
}
